// src/api/crm.ts
//
// Typed client for the CRM service (conversations, contacts, action items,
// emails, search, calendar events, connections, and OAuth).

import { getCrmServiceUrl } from '../utils/backend'
import { getSessionToken } from '../utils/sessionAuth'
import { ApiError, createApiClient } from './base'

// ---------------------------------------------------------------------------
// Internal clients
// ---------------------------------------------------------------------------

const crmClient = createApiClient(getCrmServiceUrl)

// ---------------------------------------------------------------------------
// Calendar-service error
// ---------------------------------------------------------------------------

export class CalendarConnectionRequiredError extends Error {
  constructor(message = 'Calendar connection required') {
    super(message)
    this.name = 'CalendarConnectionRequiredError'
  }
}

// ---------------------------------------------------------------------------
// crmRequest — kept for backward compatibility; wraps crmClient with the
// legacy 422 / calendar_connection_required handling.
// ---------------------------------------------------------------------------

type CrmRequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

export async function crmRequest<T = unknown>(
  path: string,
  options?: CrmRequestOptions,
): Promise<T> {
  try {
    return await crmClient.request<T>(path, options)
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      if (err.message.includes('calendar_connection_required')) {
        // Body was stringified into the error message — extract the human message if present
        const match = err.message.match(/"message"\s*:\s*"([^"]+)"/)
        throw new CalendarConnectionRequiredError(match?.[1])
      }
      // Any other 422 from the calendar service is also treated as a connection error
      throw new CalendarConnectionRequiredError()
    }
    throw err
  }
}

// ---------------------------------------------------------------------------
// Calendar service types
// ---------------------------------------------------------------------------

export type CalendarConnection = {
  id: string
  provider: 'google' | 'microsoft'
  status: 'active' | 'token_expired' | 'disabled' | 'revoked'
  last_synced_at: string | null
  consecutive_failures: number
  created_at: string
}

export type CalendarServiceEvent = {
  id: string
  title: string
  description: string | null
  location: string | null
  starts_at: string
  ends_at: string
  timezone: string | null
  all_day: boolean
  origin: string | null
  provider: 'google' | 'microsoft' | null
  notification_enabled: boolean
  notification_offset_minutes: number
}

type CalendarEventsResponse =
  | CalendarServiceEvent[]
  | { events?: CalendarServiceEvent[]; calendar_connected?: boolean }

export type FetchEventsParams = {
  startsAfter?: string
  startsBefore?: string
}

export type CreateEventParams = {
  provider: 'google' | 'microsoft'
  title: string
  starts_at: string
  ends_at: string
  location?: string | null
  timezone?: string | null
  all_day?: boolean
  notification_enabled?: boolean
  notification_offset_minutes?: number
}

export type UpdateEventParams = {
  title?: string
  starts_at?: string
  ends_at?: string
  location?: string | null
  timezone?: string | null
  all_day?: boolean
  notification_enabled?: boolean
  notification_offset_minutes?: number
}

// ---------------------------------------------------------------------------
// Calendar service functions (events, connections, OAuth)
// ---------------------------------------------------------------------------

export async function fetchMyConnections(signal?: AbortSignal): Promise<CalendarConnection[]> {
  return crmRequest('/me/connections', { signal })
}

export async function disconnectCalendar(connectionId: string): Promise<void> {
  await crmRequest(`/me/connections/${encodeURIComponent(connectionId)}`, { method: 'DELETE' })
}

export async function fetchCalendarEvents(
  params: FetchEventsParams = {},
  signal?: AbortSignal,
): Promise<CalendarServiceEvent[]> {
  const query = new URLSearchParams()
  if (params.startsAfter) query.set('starts_after', params.startsAfter)
  if (params.startsBefore) query.set('starts_before', params.startsBefore)
  const suffix = query.toString()
  const response = await crmRequest<CalendarEventsResponse>(
    `/events${suffix ? `?${suffix}` : ''}`,
    { signal },
  )
  if (Array.isArray(response)) return response
  return Array.isArray(response.events) ? response.events : []
}

export async function createCalendarEvent(
  params: CreateEventParams,
): Promise<CalendarServiceEvent> {
  return crmRequest('/events', { method: 'POST', body: params })
}

export async function updateCalendarEvent(
  id: string,
  params: UpdateEventParams,
): Promise<CalendarServiceEvent> {
  return crmRequest(`/events/${encodeURIComponent(id)}`, { method: 'PATCH', body: params })
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await crmRequest(`/events/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** Delete a calendar event via the legacy calendar-service /calendar/delete/ endpoint. */
export async function deleteCalendarServiceEvent(id: string, userId: string): Promise<void> {
  await crmClient.request('/calendar/delete/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export function buildOAuthStartUrl(provider: 'google' | 'microsoft'): string {
  const token = getSessionToken()
  if (!token) throw new Error('No session token - user must be logged in to connect a calendar')
  const params = new URLSearchParams({ token })
  return `${getCrmServiceUrl()}/oauth/${provider}/start?${params.toString()}`
}

// ---------------------------------------------------------------------------
// Calendar service — legacy calendar CRUD (/calendar/* routes)
// ---------------------------------------------------------------------------

/** Get contacts associated with a calendar event. */
export async function fetchCalendarEventClients(
  dateId: string,
  userId: string,
): Promise<ContactRecord[]> {
  const params = new URLSearchParams({ id: dateId, user_id: userId })
  return crmClient.request(`/calendar/clients/?${params.toString()}`)
}

/** List archived calendar events. */
export async function listArchivedCalendarEvents(
  userId: string,
  signal?: AbortSignal,
): Promise<CalendarItem[]> {
  const params = new URLSearchParams({ user_id: userId })
  return crmClient.request(`/calendar/list/archived/?${params.toString()}`, {
    method: 'POST',
    signal,
  })
}

/** Toggle-archive a calendar event. */
export async function archiveCalendarServiceEvent(id: string, userId: string): Promise<void> {
  await crmClient.request('/calendar/update/archive/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

/** List calendar events from the calendar service (legacy /calendar/list/ endpoint). */
export async function listCalendarServiceEvents(
  userId: string,
  signal?: AbortSignal,
): Promise<CalendarItem[]> {
  const data = await crmClient.request<CalendarItem[] | { data?: CalendarItem[] }>(
    `/calendar/list/?user_id=${encodeURIComponent(userId)}`,
    { signal },
  )
  if (Array.isArray(data)) return data
  if (data && Array.isArray((data as { data?: CalendarItem[] }).data)) {
    return (data as { data: CalendarItem[] }).data
  }
  return []
}

export type CreateCalendarServiceEventPayload = {
  user_id: string
  conversation_id: string | null
  calendar: {
    title: string
    datetime_start: string
    datetime_end: string
    location?: string
  }
}

/** Create a calendar event via the calendar service (legacy /calendar/create/ endpoint). */
export async function createCalendarServiceEvent(
  payload: CreateCalendarServiceEventPayload,
): Promise<unknown> {
  return crmClient.request('/calendar/create/', {
    method: 'POST',
    body: payload,
  })
}

// ---------------------------------------------------------------------------
// CRM service types
// ---------------------------------------------------------------------------

export type Conversation = {
  id: string
  title?: string
  transcript?: string
  summary?: string
  date_created?: string
  user_id?: string
  date_id?: string[]
  client_id?: string[]
  archived?: boolean
}

export type ConversationResponse = {
  conversation?: Conversation
  dates?: CalendarItem[]
  clients?: ContactRecord[]
  emails?: EmailDraft[]
  actionItems?: ActionItem[]
  action_items?: ActionItem[]
}

export type CalendarItem = {
  id: string
  title?: string
  date?: string
  datetime_start?: string
  datetime?: string
  datetime_end?: string
  location?: string | null
  tags?: string[]
  clients?: ContactRecord[]
  conversation_id?: string | null
  all_day?: boolean
}

export type ContactRecord = {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  cell?: string
  company?: string
  notes?: string
  summary?: string
  date_created?: string
  created_at?: string
  archived?: boolean
}

export type ActionItem = {
  id: string
  item?: string
  notes?: string
  done?: boolean
  conversation_id?: string
  date_created?: string
  due_date?: string
  dueDate?: string
}

export type ActionItemInput = {
  item: string
  notes?: string
  done?: boolean
  due_date?: string
}

export type EmailDraft = {
  id?: string
  client_email?: string[] | string
  subject?: string
  body?: string
}

export type SearchResult = {
  id: string
  type: 'CHAT' | 'CALL' | 'EML' | 'CLT' | 'AI' | 'CAL'
  title?: string
  snippet?: string
  dateCreated?: string
  tags?: string[]
}

// ---------------------------------------------------------------------------
// CRM service — conversations
// ---------------------------------------------------------------------------

export async function listConversations(
  userId: string,
  options: { clientId?: string; signal?: AbortSignal } = {},
): Promise<Conversation[]> {
  const params = new URLSearchParams({ user_id: userId })
  if (options.clientId) params.set('client_id', options.clientId)
  const result = await crmClient.request<Conversation[] | { data?: Conversation[] }>(
    `/conversation/list/?${params.toString()}`,
    { method: 'POST', signal: options.signal },
  )
  if (Array.isArray(result)) return result
  return Array.isArray((result as { data?: Conversation[] }).data)
    ? ((result as { data: Conversation[] }).data)
    : []
}

export async function listArchivedConversations(
  userId: string,
  signal?: AbortSignal,
): Promise<Conversation[]> {
  const params = new URLSearchParams({ user_id: userId })
  const result = await crmClient.request<Conversation[] | { data?: Conversation[] }>(
    `/conversation/list/archived/?${params.toString()}`,
    { method: 'POST', signal },
  )
  if (Array.isArray(result)) return result
  return Array.isArray((result as { data?: Conversation[] }).data)
    ? ((result as { data: Conversation[] }).data)
    : []
}

export async function getConversation(
  id: string,
  userId: string,
  signal?: AbortSignal,
): Promise<ConversationResponse> {
  const params = new URLSearchParams({ id, user_id: userId })
  return crmClient.request(`/conversation/?${params.toString()}`, { signal })
}

export type UpdateConversationPayload = {
  id: string
  user_id: string
  conversation: {
    title?: string
    summary?: string
    transcript?: string
    tags?: string[]
  }
}

export async function updateConversation(payload: UpdateConversationPayload): Promise<void> {
  await crmClient.request('/conversation/update/', { method: 'POST', body: payload })
}

export async function archiveConversation(id: string, userId: string): Promise<void> {
  await crmClient.request('/conversation/update/archive/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export async function deleteConversation(id: string, userId: string): Promise<void> {
  await crmClient.request('/conversation/delete/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export async function addClientToConversation(
  conversationId: string,
  userId: string,
  clientId: string,
): Promise<void> {
  await crmClient.request('/conversation/update/add-client/', {
    method: 'POST',
    body: { id: conversationId, user_id: userId, additional_id: clientId },
  })
}

export async function removeClientFromConversation(
  conversationId: string,
  userId: string,
  clientId: string,
): Promise<void> {
  await crmClient.request('/conversation/update/remove-client/', {
    method: 'POST',
    body: { id: conversationId, user_id: userId, additional_id: clientId },
  })
}

export async function addDateToConversation(
  conversationId: string,
  userId: string,
  dateId: string,
): Promise<void> {
  await crmClient.request('/conversation/update/add-date/', {
    method: 'POST',
    body: { id: conversationId, user_id: userId, additional_id: dateId },
  })
}

export async function removeDateFromConversation(
  conversationId: string,
  userId: string,
  dateId: string,
): Promise<void> {
  await crmClient.request('/conversation/update/remove-date/', {
    method: 'POST',
    body: { id: conversationId, user_id: userId, additional_id: dateId },
  })
}

// ---------------------------------------------------------------------------
// CRM service — contacts (clients)
// ---------------------------------------------------------------------------

export async function listClients(userId: string, signal?: AbortSignal): Promise<ContactRecord[]> {
  const params = new URLSearchParams({ user_id: userId })
  return crmClient.request(`/client/list/?${params.toString()}`, { signal })
}

export async function listArchivedClients(
  userId: string,
  signal?: AbortSignal,
): Promise<ContactRecord[]> {
  const params = new URLSearchParams({ user_id: userId })
  return crmClient.request(`/client/list/archived/?${params.toString()}`, {
    method: 'POST',
    signal,
  })
}

export type CreateClientPayload = {
  user_id: string
  conversation_id: string | null
  client: {
    first_name: string
    last_name: string
    email: string
    cell: string
    company: string
    notes: string
  }
}

export async function createClient(payload: CreateClientPayload): Promise<ContactRecord> {
  const result = await crmClient.request<
    string | { id?: string; client_id?: string } | ContactRecord
  >('/client/create/', { method: 'POST', body: payload })
  if (typeof result === 'string') return { id: result }
  return result as ContactRecord
}

export async function getClient(
  id: string,
  userId: string,
  signal?: AbortSignal,
): Promise<ContactRecord> {
  const params = new URLSearchParams({ id, user_id: userId })
  return crmClient.request(`/client/?${params.toString()}`, { signal })
}

export type UpdateClientPayload = {
  id: string
  user_id: string
  client: {
    first_name?: string
    last_name?: string
    email?: string
    cell?: string
    company?: string
    notes?: string
  }
}

export async function updateClient(payload: UpdateClientPayload): Promise<void> {
  await crmClient.request('/client/update/', { method: 'POST', body: payload })
}

export async function archiveClient(id: string, userId: string): Promise<void> {
  await crmClient.request('/client/update/archive/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export async function deleteClient(id: string, userId: string): Promise<void> {
  await crmClient.request('/client/delete/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

// ---------------------------------------------------------------------------
// CRM service — action items
// ---------------------------------------------------------------------------

export async function listActionItems(
  userId: string,
  signal?: AbortSignal,
): Promise<ActionItem[]> {
  const params = new URLSearchParams({ user_id: userId })
  const result = await crmClient.request<ActionItem[] | { data?: ActionItem[] }>(
    `/action-item/list/?${params.toString()}`,
    { signal },
  )
  if (Array.isArray(result)) return result
  return Array.isArray((result as { data?: ActionItem[] }).data)
    ? ((result as { data: ActionItem[] }).data)
    : []
}

export async function listArchivedActionItems(
  userId: string,
  signal?: AbortSignal,
): Promise<ActionItem[]> {
  const params = new URLSearchParams({ user_id: userId })
  const result = await crmClient.request<ActionItem[] | { data?: ActionItem[] }>(
    `/action-item/list/archived/?${params.toString()}`,
    { signal },
  )
  if (Array.isArray(result)) return result
  return Array.isArray((result as { data?: ActionItem[] }).data)
    ? ((result as { data: ActionItem[] }).data)
    : []
}

export type CreateActionItemPayload = {
  user_id: string
  conversation_id: string
  action_item: ActionItemInput
}

export async function createActionItem(payload: CreateActionItemPayload): Promise<ActionItem> {
  const result = await crmClient.request<ActionItem | { data?: ActionItem }>(
    '/action-item/create/',
    { method: 'POST', body: payload },
  )
  if (result && typeof result === 'object' && 'data' in result && result.data) return result.data
  return result as ActionItem
}

export type UpdateActionItemPayload = {
  id: string
  user_id: string
  conversation_id: string
  action_item: ActionItemInput
}

export async function updateActionItem(payload: UpdateActionItemPayload): Promise<void> {
  await crmClient.request('/action-item/update/', { method: 'POST', body: payload })
}

export async function toggleActionItemDone(id: string, userId: string): Promise<void> {
  await crmClient.request('/action-item/toggle-done/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export async function archiveActionItem(id: string, userId: string): Promise<void> {
  await crmClient.request('/action-item/update/archive/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

export async function deleteActionItem(id: string, userId: string): Promise<void> {
  await crmClient.request('/action-item/delete/', {
    method: 'POST',
    body: { id, user_id: userId },
  })
}

// ---------------------------------------------------------------------------
// CRM service — emails
// ---------------------------------------------------------------------------

export type UpdateEmailPayload = {
  id: string
  user_id: string
  conversation_id?: string
  email: {
    subject?: string
    body?: string
    client_email?: string | string[]
  }
}

export async function updateEmail(payload: UpdateEmailPayload): Promise<void> {
  await crmClient.request('/email/update/', { method: 'POST', body: payload })
}

// ---------------------------------------------------------------------------
// CRM service — calendar-event client relations
// ---------------------------------------------------------------------------

/** Add a contact to a calendar event (CRM service). */
export async function addClientToCalendarEvent(
  dateId: string,
  userId: string,
  clientId: string,
): Promise<void> {
  await crmClient.request('/calendar/update/add-client/', {
    method: 'POST',
    body: { id: dateId, user_id: userId, additional_id: clientId },
  })
}

/** Remove a contact from a calendar event (CRM service). */
export async function removeClientFromCalendarEvent(
  dateId: string,
  userId: string,
  clientId: string,
): Promise<void> {
  await crmClient.request('/calendar/update/remove-client/', {
    method: 'POST',
    body: { id: dateId, user_id: userId, additional_id: clientId },
  })
}

// ---------------------------------------------------------------------------
// CRM service — search
// ---------------------------------------------------------------------------

export async function searchCrm(query: string, userId: string): Promise<SearchResult[]> {
  const params = new URLSearchParams({ query, user_id: userId })
  const result = await crmClient.request<SearchResult[] | null | undefined>(
    `/search/?${params.toString()}`,
  )
  return Array.isArray(result) ? result : []
}
